import { Divider } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/divider';
import { Photo } from '../sections/photo';
import { WeddingOverview } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/sections/wedding-overview/wedding-overview';
import { SECTION_COMPONENTS } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/sections/section-components';
import {
  SectionEntry,
  arrangeSections,
  buildContentSections,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/sections/arrange-sections';
import { useWeddingWebsiteData } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/data/data-context';
import { useIsEditMode } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/data/data-provider-panel';
import { isPhotoWeddingSection } from '@bridebook/web/lib/wedding-website/utils/is-photo-wedding-section';
import { AnimatePresence, LazyMotion, LayoutGroup } from 'motion/react';
import { loadDomMax } from '@bridebook/aura';
import { useMemo } from 'react';
import { AnimatedSection } from '@bridebook/web/components/wedding-website/view-mode/partials/animated-section';

// only between same sections, no divider between content and photo sections
const shouldRenderDivider = (prev: SectionEntry, curr: SectionEntry): boolean =>
  prev.kind === curr.kind;

/**
 * Static section component for guest view (no animations)
 */
const Section = ({ section, showDivider }: { section: SectionEntry; showDivider: boolean }) => (
  <>
    {/* photos do not have padding by default, which would cause them to stick together and make the divider barely visible  */}
    {showDivider &&
      (section.kind === 'photo' ? <div className="pt-[60px] @screen-lg:pt-[80px]" /> : <Divider />)}
    {section.element}
  </>
);

export const SectionsWithDividers = () => {
  const { website } = useWeddingWebsiteData();
  const isEditMode = useIsEditMode();
  const websitePhotosSection = website.sections.find(isPhotoWeddingSection);

  // Build content sections dynamically based on the order in website.sections
  const contentSections = useMemo(
    () =>
      buildContentSections(website.sections, {
        overview: <WeddingOverview />,
        registry: SECTION_COMPONENTS,
      }),
    [website.sections],
  );

  const visibleContentSections = contentSections.filter((section) => section.visible);

  const photoSections: SectionEntry[] = [
    {
      key: 'photo-0',
      kind: 'photo',
      visible: !!websitePhotosSection?.data[0]?.path,
      element: <Photo index={0} />,
    },
    {
      key: 'photo-1',
      kind: 'photo',
      visible: !!websitePhotosSection?.data[1]?.path,
      element: <Photo index={1} />,
    },
    {
      key: 'photo-2',
      kind: 'photo',
      visible: !!websitePhotosSection?.data[2]?.path,
      element: <Photo index={2} />,
    },
  ];

  // Future proofing: Filtered again to remove photos that are not uploaded to a given slot or hidden
  const orderedVisibleSections = arrangeSections(visibleContentSections, photoSections).filter(
    (section) => section.visible,
  );

  // In edit mode, use layout animations for smooth reordering
  if (isEditMode) {
    return (
      <LazyMotion features={loadDomMax}>
        <LayoutGroup>
          <AnimatePresence mode="popLayout">
            {orderedVisibleSections.map((section, index) => {
              const prev = orderedVisibleSections[index - 1];
              const showDivider = index > 0 && shouldRenderDivider(prev, section);
              return (
                <AnimatedSection key={section.key} layoutId={section.key}>
                  {showDivider &&
                    (section.kind === 'photo' ? (
                      <div className="pt-[60px] @screen-lg:pt-[80px]" />
                    ) : (
                      <Divider />
                    ))}
                  {section.element}
                </AnimatedSection>
              );
            })}
          </AnimatePresence>
        </LayoutGroup>
      </LazyMotion>
    );
  }

  // In guest view, render without motion for better performance
  return (
    <>
      {orderedVisibleSections.map((section, index) => {
        const prev = orderedVisibleSections[index - 1];
        const showDivider = index > 0 && shouldRenderDivider(prev, section);
        return <Section key={section.key} section={section} showDivider={showDivider} />;
      })}
    </>
  );
};
